import React, { useState, useEffect } from 'react';
import { submissionsApi } from '../../api/Submission';
import {
  SpaceBetween,
  Container,
  Box,
  ProgressBar,
  Spinner,
  Header,
  TextContent,
  Alert,
  Badge,
  StatusIndicator
} from '@cloudscape-design/components';
import { parseAiRecommendationPercentageScore } from '../../common/utility'

const RecommendationsList = ({ recommendations }) => {
  if (!recommendations || !recommendations.description_recommendations || !recommendations.admin_recommendations) {
    return <Box>No recommendations available</Box>;
  }

  return (
    <SpaceBetween size="m">
      {recommendations.description_recommendations.map((rec, idx) => (
        <Container key={`desc-${idx}`}>
          <Box variant="p" fontWeight="bold">• {rec.point}</Box>
          <Box variant="p" color="text-body-secondary">{rec.analysis}</Box>
        </Container>
      ))}
      
      {recommendations.admin_recommendations.map((rec, idx) => (
        <Container key={`admin-${idx}`}>
          <Box variant="p" fontWeight="bold">• {rec.point}</Box>
          <Box variant="p" color="text-body-secondary">{rec.analysis}</Box>
        </Container>
      ))}
    </SpaceBetween>
  );
};

const IndicatorCard = ({ indicator }) => {
  const hasRecommendations = indicator.latest_recommendation?.details?.recommendations;
const score = indicator.latest_recommendation?.score;
const displayScore = (score ?? null) === null ? "n/a" : `${parseAiRecommendationPercentageScore(score)}%`;

  return (
    <Container
      header={
        <Header 
          variant="h3"
          info={
            <Box float="right">
              <Badge color="blue">
                AI Score: {displayScore}
              </Badge>
            </Box>
          }
        >
          {indicator.indicator_index}: {indicator.indicator_name || 'Unnamed Indicator'}
        </Header>
      }
    >
      <SpaceBetween size="m">
        {/* Show recommendations if they exist */}
        {hasRecommendations && (
          <Box>
            <Box variant="awsui-key-label">AI Recommendations:</Box>
            <RecommendationsList recommendations={indicator.latest_recommendation.details.recommendations} />
          </Box>
        )}
      </SpaceBetween>
    </Container>
  );
};

const StandardSection = ({ standard }) => {
  return (
    <Container
      header={
        <Header variant="h2">
          {standard.name}
        </Header>
      }
    >
      <SpaceBetween size="l">
        {standard.indicator_submissions?.map(indicator => (
          <IndicatorCard key={indicator.indicator_submission_id} indicator={indicator} />
        ))}
      </SpaceBetween>
    </Container>
  );
};

export default function SubmissionDetails({ applicationID }) {
  const [standards, setStandards] = useState([]);
  const [applicationScore, setApplicationScore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const data = await submissionsApi.getStandardsIndicators(applicationID);
        setApplicationScore(data.avg_score)
        setStandards(data.standards);
      } catch (err) {
        setError(err);
        console.error('Error fetching submission data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [applicationID]);

  if (loading) {
    return (
      <Box textAlign="center" padding={{ top: "xxl" }}>
        <Spinner size="large" />
        <Box variant="h2" padding={{ top: "s" }}>Loading submission details...</Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box textAlign="center" padding={{ top: "xxl" }}>
        <StatusIndicator type="error">
          Error loading submission details
        </StatusIndicator>
      </Box>
    );
  }

  return (
    <SpaceBetween size="l">
      <Alert type="info">
        The following is a summary of the AI findings related to the clarity and completeness of your Institutional Listing Application. It is highly recommended that you address the following recommendations before submitting your application through the Qualifications Management System (QMS).
      </Alert>
      <Box float="left">
              <Badge color="blue">
                Application AI Score:  {applicationScore ? `${parseAiRecommendationPercentageScore(applicationScore)}%` : "n/a" }
              </Badge>
    </Box>
      {standards.map(standard => (
        <StandardSection key={standard.id} standard={standard} />
      ))}
    </SpaceBetween>
  );
}